(() => {
  if (customElements.get('product-mo-faq')) return;

  class ProductMoFaq extends HTMLElement {
    connectedCallback() {
      if (this.dataset.productMoInitialized === 'true') return;
      this.dataset.productMoInitialized = 'true';
      this.addEventListener('click', (event) => {
        const button = event.target.closest('.product-mo-faq__question');
        if (!button || !this.contains(button)) return;
        this.toggle(button);
      });
    }

    toggle(button) {
      const panel = document.getElementById(button.getAttribute('aria-controls'));
      if (!panel) return;

      const willOpen = button.getAttribute('aria-expanded') !== 'true';
      if (this.dataset.allowMultiple !== 'true') {
        this.querySelectorAll('.product-mo-faq__question[aria-expanded="true"]').forEach((openButton) => {
          if (openButton === button) return;
          const openPanel = document.getElementById(openButton.getAttribute('aria-controls'));
          openButton.setAttribute('aria-expanded', 'false');
          if (openPanel) openPanel.hidden = true;
        });
      }

      button.setAttribute('aria-expanded', String(willOpen));
      panel.hidden = !willOpen;
    }
  }

  class ProductMoGallery extends HTMLElement {
    connectedCallback() {
      if (this.dataset.productMoInitialized === 'true') return;
      this.dataset.productMoInitialized = 'true';
      this.track = this.querySelector('.product-mo-gallery__track');
      this.querySelector('[data-product-mo-prev]')?.addEventListener('click', () => this.scroll(-1));
      this.querySelector('[data-product-mo-next]')?.addEventListener('click', () => this.scroll(1));
      this.track?.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.scroll(-1);
        if (event.key === 'ArrowRight') this.scroll(1);
      });
    }

    scroll(direction) {
      if (!this.track) return;
      const item = this.track.querySelector('.product-mo-gallery__item');
      const amount = item ? item.getBoundingClientRect().width + 20 : this.track.clientWidth;
      this.track.scrollBy({ left: amount * direction, behavior: 'smooth' });
    }
  }

  class ProductMoYoutubeGallery extends HTMLElement {
    connectedCallback() {
      if (this.dataset.productMoInitialized === 'true') return;
      this.dataset.productMoInitialized = 'true';
      this.stage = this.querySelector('[data-youtube-stage]');
      this.main = this.querySelector('.product-mo-youtube-gallery__main');
      this.thumbnails = this.querySelector('.product-mo-youtube-gallery__thumbnails');
      this.connector = this.querySelector('[data-youtube-connector]');
      this.handleConnectorUpdate = () => {
        this.updateListHeight();
        this.updateConnector();
      };
      this.mainResizeObserver = globalThis.ResizeObserver ? new globalThis.ResizeObserver(this.handleConnectorUpdate) : null;
      if (this.main) this.mainResizeObserver?.observe(this.main);
      this.thumbnails?.addEventListener('scroll', this.handleConnectorUpdate, { passive: true });
      globalThis.addEventListener?.('resize', this.handleConnectorUpdate, { passive: true });
      globalThis.requestAnimationFrame?.(this.handleConnectorUpdate);
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-youtube-select], [data-youtube-play]');
        if (!button || !this.contains(button)) return;

        if (button.matches('[data-youtube-select]')) {
          this.selectVideo(button);
          return;
        }

        this.playVideo(button);
      });
    }

    selectVideo(button) {
      const videoId = button.dataset.videoId?.trim();
      if (!videoId) return;
      this.updateSelection(button);
      this.updateMetadata(button);
      if (this.stage?.querySelector('iframe')) {
        this.playVideo(button);
        return;
      }
      this.renderPreview(button);
    }

    updateSelection(button) {
      const videoPosition = button.dataset.videoPosition;
      if (!videoPosition) return;
      this.querySelectorAll('[data-youtube-select]').forEach((item) => {
        item.setAttribute('aria-pressed', String(item.dataset.videoPosition === videoPosition));
      });
      globalThis.requestAnimationFrame?.(this.handleConnectorUpdate);
    }

    updateConnector() {
      const selected = this.querySelector('[data-youtube-select][aria-pressed="true"]');
      const layout = this.querySelector('[data-youtube-layout]');
      if (!this.connector?.style || !selected?.getBoundingClientRect || !this.stage?.getBoundingClientRect || !layout?.getBoundingClientRect) return;
      const layoutRect = layout.getBoundingClientRect();
      const selectedRect = selected.getBoundingClientRect();
      const stageRect = this.stage.getBoundingClientRect();
      const aboveStage = selectedRect.bottom <= stageRect.top;
      const belowStage = selectedRect.top >= stageRect.bottom;
      const vertical = selectedRect.bottom <= stageRect.top || selectedRect.top >= stageRect.bottom;
      const left = vertical ? selectedRect.left + selectedRect.width / 2 - layoutRect.left : stageRect.right - layoutRect.left;
      const top = aboveStage
        ? selectedRect.bottom - layoutRect.top
        : belowStage
          ? stageRect.bottom - layoutRect.top
          : selectedRect.top + selectedRect.height / 2 - layoutRect.top;
      const width = vertical ? 2 : Math.max(0, selectedRect.left - stageRect.right);
      const height = aboveStage
        ? Math.max(0, stageRect.top - selectedRect.bottom)
        : belowStage
          ? Math.max(0, selectedRect.top - stageRect.bottom)
          : 2;
      this.connector.style.setProperty('--youtube-connector-left', `${left}px`);
      this.connector.style.setProperty('--youtube-connector-top', `${top}px`);
      this.connector.style.setProperty('--youtube-connector-width', `${width}px`);
      this.connector.style.setProperty('--youtube-connector-height', `${height}px`);
    }

    updateListHeight() {
      if (!this.style || !this.main?.getBoundingClientRect) return;
      const height = this.main.getBoundingClientRect().height;
      if (height > 0) this.style.setProperty('--youtube-gallery-main-height', `${height}px`);
    }

    disconnectedCallback() {
      this.thumbnails?.removeEventListener('scroll', this.handleConnectorUpdate);
      globalThis.removeEventListener?.('resize', this.handleConnectorUpdate);
      this.mainResizeObserver?.disconnect();
    }

    updateMetadata(button) {
      const metadata = this.querySelector('[data-youtube-metadata]');
      if (!metadata) return;
      const author = button.dataset.videoAuthor?.trim() || '';
      const heading = button.dataset.videoHeading?.trim() || '';
      const authorElement = metadata.querySelector('[data-youtube-metadata-author]');
      const headingElement = metadata.querySelector('[data-youtube-metadata-heading]');
      const separator = metadata.querySelector('[data-youtube-metadata-separator]');
      if (authorElement) authorElement.textContent = author;
      if (headingElement) headingElement.textContent = heading;
      if (separator) separator.hidden = !(author && heading);
      metadata.hidden = !(author || heading);
    }

    renderPreview(button) {
      const videoId = button.dataset.videoId?.trim();
      if (!videoId || !this.stage) return;
      const title = button.dataset.videoTitle || 'YouTube video';

      const preview = document.createElement('button');
      preview.className = 'product-mo-youtube-gallery__preview';
      preview.type = 'button';
      preview.dataset.youtubePlay = '';
      preview.dataset.videoId = videoId;
      preview.dataset.videoPosition = button.dataset.videoPosition;
      preview.dataset.videoTitle = title;
      preview.dataset.videoAuthor = button.dataset.videoAuthor || '';
      preview.dataset.videoHeading = button.dataset.videoHeading || '';

      const image = document.createElement('img');
      image.src = `https://i.ytimg.com/vi/${encodeURIComponent(videoId)}/maxresdefault.jpg`;
      image.width = 480;
      image.height = 360;
      image.loading = 'lazy';
      image.alt = '';

      const label = document.createElement('span');
      label.className = 'visually-hidden';
      label.textContent = `Play ${title}`;

      preview.append(image, label);
      this.stage.replaceChildren(preview);
    }

    playVideo(button) {
      const videoId = button.dataset.videoId?.trim();
      if (!videoId || !this.stage) return;
      this.updateSelection(button);
      this.updateMetadata(button);

      const iframe = document.createElement('iframe');
      iframe.src = `https://www.youtube.com/embed/${encodeURIComponent(videoId)}?autoplay=1&rel=0`;
      iframe.title = button.dataset.videoTitle || 'YouTube video';
      iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
      iframe.allowFullscreen = true;
      this.stage.replaceChildren(iframe);
    }
  }

  customElements.define('product-mo-faq', ProductMoFaq);
  customElements.define('product-mo-gallery', ProductMoGallery);
  customElements.define('product-mo-youtube-gallery', ProductMoYoutubeGallery);
})();
